import { Check, Lock } from '@huspy/briks-icons';
import {
  Box, Flex, Text
} from '@huspy/briks-web';
import { VStack } from '@huspy/styled-system/jsx';
import { Accordion, Skeleton } from '@mantine/core';
import {
  createRoute, Link, useLocation,
} from '@tanstack/react-router';
import { memo, JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { progressionStepperStyles } from './recipes';
import { getFirstThreeSegments, replaceDynamicSegments } from './utils';

export namespace ProgresionStepperTypes {
  export type LinkProps<T extends ReturnType<typeof createRoute> = ReturnType<typeof createRoute>> = {
    icon: JSX.Element;
    name: string;
    to: T['to'];
    search?: T['types']['searchSchema'];
    params?: T['types']['allParams'];
    isAccessible: boolean;
    isComplete?: boolean;
    subtitle?: string;
    sublinks: {
      name: string;
      to?: T['to'] | `${T['to']}${string}`;
      search: T['types']['searchSchema'];
      params: T['types']['allParams'];
      isAccessible?: boolean;
      isComplete?: boolean;
    }[];
    accessibleText?: string;
  };
}

// eslint-disable-next-line react/display-name
const StepCard = memo(({
  icon,
  name,
  to,
  params,
  search,
  sublinks,
  subtitle,
  isAccessible,
  isComplete,
  accessibleText,
}: ProgresionStepperTypes.LinkProps<any>) => (
  <Link to={ to } params={ params } search={ search } width='100%' disabled={ !isAccessible }>
    {(props) => (
      <VStack
        py='3'
        px='5'
        width='100%'
        className={ progressionStepperStyles.card({ active: props.isActive, isAccessible }) }
      >
        <Flex justify='space-between' alignItems='center' w='100%'>
          <Flex gap='2,5' alignItems='center'>
            {icon}
            <Box>
              <Text size='lg' weight='medium'>{name}</Text>
              {!isComplete && <Text size='md' textAlign='left'>{subtitle}</Text>}
            </Box>
          </Flex>
          {isComplete !== undefined && isComplete ? (
            <Box bg='utility-indigo-800' borderRadius='full' p='1.5'>
              <Check color='#fff' width={ 12 } height={ 12 } />
            </Box>
          ) : (
            <Flex justifyContent='center' alignItems='center'>
              {isAccessible ? (
                <Text
                  data-test='step-card-status'
                  size='md'
                  color={ props.isActive ? 'utility-indigo-800' : 'text-secondary' }
                >
                  {accessibleText ?? 'Pending'}
                </Text>
              ) : (
                <Lock />
              )}
            </Flex>
          )}
        </Flex>
        {sublinks.length > 0 && (
          <Accordion.Panel w='100%'>
            <VStack w='100%' gap='1' flexDir='column'>
              {sublinks?.map((sublink, i) => (
                <Link
                  to={ sublink.to ?? to }
                  params={ sublink.params }
                  search={ sublink.search }
                  width='100%'
                  style={ { width: '100%' } }
                  // eslint-disable-next-line react/no-array-index-key
                  key={ `${sublink.name}-${i}` }
                  onClick={ (e) => e.stopPropagation() }
                  disabled={ sublink.isAccessible !== undefined && !sublink.isAccessible }
                >
                  {({ isActive }) => (
                    <Flex
                      className={ progressionStepperStyles.sublink(
                        {
                          isAccessible: sublink.isAccessible === undefined || sublink.isAccessible,
                          isActive,
                        }
                      ) }
                    >
                      <Text size='lg' weight='medium' textAlign='left' color='unset'>
                        {sublink.name}
                      </Text>
                      {sublink.isAccessible !== undefined && !sublink.isAccessible && (
                        <Lock style={ {
                          position: 'relative',
                          left: '10px',
                        } }
                        />
                      )}
                      {sublink.isComplete && (
                        <Box
                          bg='indigo.100'
                          borderRadius='full'
                          p='0.5'
                          display='flex'
                          alignItems='center'
                          position='relative'
                          left='10px'
                        >
                          <Check
                            width={ 12 }
                            height={ 12 }
                            color='#6366F1'
                          />
                        </Box>
                      )}
                    </Flex>
                  )}
                </Link>
              ))}
            </VStack>
          </Accordion.Panel>
        )}
      </VStack>
    )}
  </Link>
));

// eslint-disable-next-line react/display-name
const StepCardSkeleton = () => <Skeleton w='100%' height='50px' radius='12px' />;

const ProgressionStepper = ({
  routeId,
  isLoading,
  header,
  footer,
  links,
}: {
  routeId: string,
  isLoading: boolean;
  header: React.ReactNode;
  footer?: React.ReactNode;
  links: ProgresionStepperTypes.LinkProps[],
}) => {
  const { t } = useTranslation();
  const { pathname } = useLocation();

  return (
    <VStack alignItems='flex-start' gap='spacing-10'>
      <VStack alignItems='flex-start' gap='1'>
        {header}
      </VStack>
      <VStack gap='spacing-5' width='100%' alignItems='flex-start'>
        <Text color='text-secondary' size='md'>
          {t(
            'opportunity.caseSubmission.asideContent.navigateThroughSections'
          )}
        </Text>
        <Accordion
          unstyled
          w='100%'
          chevron={ false }
          value={ getFirstThreeSegments(pathname) }
        >
          <VStack gap='spacing-3' width='100%' alignItems='flex-start'>
            {links.map(({
              icon, name, to, sublinks, subtitle, isAccessible, isComplete, params, search,
              accessibleText,
            }) => (
              <Accordion.Item
                key={ name }
                value={ getFirstThreeSegments(replaceDynamicSegments(to, routeId)) }
                w='100%'
                display='flex'
              >
                <Accordion.Control w='100%'>
                  {isLoading ? (
                    <StepCardSkeleton />
                  ) : (
                    <StepCard
                      icon={ icon }
                      name={ name }
                      isComplete={ isComplete }
                      isAccessible={ isAccessible }
                      to={ replaceDynamicSegments(to, routeId) }
                      sublinks={ sublinks }
                      subtitle={ subtitle }
                      params={ params }
                      search={ search }
                      accessibleText={ accessibleText }
                    />
                  )}
                </Accordion.Control>
              </Accordion.Item>
            ))}
          </VStack>
        </Accordion>
        {footer}
      </VStack>
    </VStack>
  );
};

export default ProgressionStepper;
